import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';

type Props = {
  chips: string[];
  onChange: (chips: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  className?: string;
};

/** Chip filter: type text and press Enter to add; click suggestions; remove chips with ×. */
export const FilterChipsInput: React.FC<Props> = ({
  chips,
  onChange,
  suggestions = [],
  placeholder,
  className = '',
}) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addChip = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (chips.some(c => c.toLowerCase() === v.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...chips, v]);
    setDraft('');
  };

  const removeChip = (idx: number) => {
    onChange(chips.filter((_, i) => i !== idx));
  };

  const toggleSuggestion = (s: string) => {
    const i = chips.findIndex(c => c.toLowerCase() === s.toLowerCase());
    if (i >= 0) onChange(chips.filter((_, j) => j !== i));
    else onChange([...chips, s]);
  };

  const unusedSuggestions = suggestions.filter(
    s => !chips.some(c => c.toLowerCase() === s.toLowerCase()),
  );

  return (
    <div className={className}>
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[2.75rem] rounded-xl border border-sky-200/80 dark:border-slate-600 bg-white/90 dark:bg-slate-800/80 px-2.5 py-2 focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-400 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map((chip, i) => (
          <span
            key={`${chip}-${i}`}
            className="inline-flex items-center gap-1 max-w-full rounded-lg bg-sky-100 dark:bg-sky-900/50 text-sky-900 dark:text-sky-100 pl-2.5 pr-1 py-1 text-xs font-medium border border-sky-200/70 dark:border-sky-800/60"
          >
            <span className="truncate">{chip}</span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                removeChip(i);
              }}
              className="shrink-0 rounded-md p-0.5 hover:bg-sky-200/80 dark:hover:bg-sky-800/80 text-sky-700 dark:text-sky-200"
              aria-label="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addChip(draft);
            } else if (e.key === 'Backspace' && !draft && chips.length) {
              removeChip(chips.length - 1);
            }
          }}
          placeholder={chips.length ? '' : placeholder}
          className="flex-1 min-w-[7rem] bg-transparent border-0 outline-none text-sm text-stone-800 dark:text-slate-100 placeholder:text-stone-400 dark:placeholder:text-slate-500 py-1"
        />
      </div>
      {unusedSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {unusedSuggestions.slice(0, 16).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => toggleSuggestion(s)}
              className="rounded-lg border border-dashed border-stone-300 dark:border-slate-600 px-2.5 py-1 text-[11px] text-stone-600 dark:text-slate-300 hover:border-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/40 hover:text-sky-800 dark:hover:text-sky-200 transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
